
import React from 'react';
import { Department, StaffMember } from '../types';
import { Icons } from './ui/Icons';

interface StaffWorkingHoursPrintPageProps {
    startDate: string;
    endDate: string;
    week1Dates: string[];
    week2Dates: string[];
    reportData: any[];
    propertyName: string;
    onDone: () => void;
    selectedDeptIds?: string[];
}

const StaffWorkingHoursPrintPage: React.FC<StaffWorkingHoursPrintPageProps> = ({
    startDate,
    endDate,
    week1Dates,
    week2Dates,
    reportData,
    propertyName,
    onDone,
    selectedDeptIds = ['all']
}) => {
    const handlePrint = () => {
        window.print();
    };

    const isAll = selectedDeptIds.includes('all');
    const filteredReportData = reportData;

    const renderTable = (deptGroup: any, isSingle: boolean) => (
        <div key={deptGroup.department.id} className={!isSingle ? "page-break mb-12" : ""}>
            <div className="text-center mb-8">
                <h1 className="text-2xl font-black uppercase tracking-widest mb-2">
                    {propertyName} - STAFF WORKING HOURS
                </h1>
                <h2 className="text-xl font-bold uppercase tracking-widest text-indigo-600">
                    PAY PERIOD FROM: {new Date(startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} 
                    TO {new Date(endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}, {new Date(startDate).getFullYear()}
                </h2>
                <div className="mt-2 text-lg font-black uppercase text-gray-700">
                    Department: {deptGroup.department.name}
                </div>
            </div>

            <table className="w-full border-collapse text-[10px]">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-400 px-2 py-2 text-left w-48 font-black">NAME</th>
                        {week1Dates.map(date => (
                            <th key={date} className="border border-gray-400 px-1 py-2 text-center w-16 font-black">
                                {new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                            </th>
                        ))}
                        <th className="border border-gray-400 px-2 py-2 text-center bg-amber-100 font-black w-20">Total wk1</th>
                        {week2Dates.map(date => (
                            <th key={date} className="border border-gray-400 px-1 py-2 text-center w-16 font-black">
                                {new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                            </th>
                        ))}
                        <th className="border border-gray-400 px-2 py-2 text-center bg-amber-100 font-black w-20">Total wk2</th>
                        <th className="border border-gray-400 px-2 py-2 text-center bg-indigo-100 font-black w-24">Grand Total</th>
                    </tr>
                    <tr className="bg-gray-50">
                        <th className="border border-gray-400 px-2 py-1 text-left"></th>
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                            <th key={`d1-${day}`} className="border border-gray-400 px-1 py-1 text-center font-bold text-[8px] uppercase">{day}</th>
                        ))}
                        <th className="border border-gray-400"></th>
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                            <th key={`d2-${day}`} className="border border-gray-400 px-1 py-1 text-center font-bold text-[8px] uppercase">{day}</th>
                        ))}
                        <th className="border border-gray-400"></th>
                        <th className="border border-gray-400"></th>
                    </tr>
                </thead>
                <tbody>
                    {deptGroup.staffRows.map(row => (
                        <tr key={row.staff.id} className="hover:bg-gray-50 transition-colors">
                            <td className="border border-gray-400 px-2 py-2 font-bold uppercase">{row.staff.name}</td>
                            {row.week1Hours.map((h, i) => (
                                <td key={`h1-${i}`} className={`border border-gray-400 px-1 py-2 text-center font-mono ${h > 0 ? 'text-blue-700 font-bold' : 'text-gray-400'}`}>
                                    {h > 0 ? h.toFixed(2) : '-'}
                                </td>
                            ))}
                            <td className="border border-gray-400 px-2 py-2 text-center font-mono font-black bg-amber-50">{row.totalWk1.toFixed(2)}</td>
                            {row.week2Hours.map((h, i) => (
                                <td key={`h2-${i}`} className={`border border-gray-400 px-1 py-2 text-center font-mono ${h > 0 ? 'text-blue-700 font-bold' : 'text-gray-400'}`}>
                                    {h > 0 ? h.toFixed(2) : '-'}
                                </td>
                            ))}
                            <td className="border border-gray-400 px-2 py-2 text-center font-mono font-black bg-amber-50">{row.totalWk2.toFixed(2)}</td>
                            <td className="border border-gray-400 px-2 py-2 text-center font-mono font-black bg-indigo-50 text-indigo-800">{row.grandTotal.toFixed(2)}</td>
                        </tr>
                    ))}
                    <tr className="bg-gray-100 font-black">
                        <td className="border border-gray-400 px-2 py-2 text-right uppercase">Dept Total :</td>
                        {week1Dates.map((_, i) => (
                            <td key={`dt1-${i}`} className="border border-gray-400 px-1 py-2 text-center font-mono">
                                {deptGroup.staffRows.reduce((sum, r) => sum + r.week1Hours[i], 0).toFixed(2)}
                            </td>
                        ))}
                        <td className="border border-gray-400 px-2 py-2 text-center font-mono bg-amber-100">
                            {deptGroup.staffRows.reduce((sum, r) => sum + r.totalWk1, 0).toFixed(2)}
                        </td>
                        {week2Dates.map((_, i) => (
                            <td key={`dt2-${i}`} className="border border-gray-400 px-1 py-2 text-center font-mono">
                                {deptGroup.staffRows.reduce((sum, r) => sum + r.week2Hours[i], 0).toFixed(2)}
                            </td>
                        ))}
                        <td className="border border-gray-400 px-2 py-2 text-center font-mono bg-amber-100">
                            {deptGroup.staffRows.reduce((sum, r) => sum + r.totalWk2, 0).toFixed(2)}
                        </td>
                        <td className="border border-gray-400 px-2 py-2 text-center font-mono bg-indigo-100 text-indigo-900">
                            {deptGroup.staffRows.reduce((sum, r) => sum + r.grandTotal, 0).toFixed(2)}
                        </td>
                    </tr>
                </tbody>
            </table>

            <div className="mt-12 grid grid-cols-2 gap-20 px-10">
                <div className="border-t-2 border-gray-900 pt-2 text-center font-black uppercase text-[10px]">
                    Manager Signature
                </div>
                <div className="border-t-2 border-gray-900 pt-2 text-center font-black uppercase text-[10px]">
                    Date
                </div>
            </div>

            <div className="mt-8 text-center text-[8px] text-gray-400 uppercase tracking-widest">
                Generated on {new Date().toLocaleString()} • Staff Working Hours Report • {propertyName}
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-white p-8 print:p-0 font-sans text-gray-900">
            {/* Controls - Hidden in print */}
            <div className="max-w-full mx-auto mb-8 flex justify-between items-center print:hidden no-print">
                <button 
                    onClick={onDone}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-bold"
                >
                    <Icons.ArrowLeft className="w-4 h-4" />
                    Back to Reports
                </button>
                <button 
                    onClick={handlePrint}
                    className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-all shadow-md"
                >
                    <Icons.Printer className="w-4 h-4" />
                    Print Report
                </button>
            </div>

            <div className="w-full mx-auto">
                {filteredReportData.map(deptGroup => renderTable(deptGroup, filteredReportData.length === 1))}
                
                {isAll && (
                    <div className="mt-12 pt-8 border-t-4 border-gray-900 page-break">
                        <h3 className="text-xl font-black text-center uppercase mb-6">Grand Summary (All Departments)</h3>
                        <table className="w-full border-collapse text-[10px]">
                            <thead>
                                <tr className="bg-gray-900 text-white font-black">
                                    <th className="border border-gray-800 px-4 py-3 text-right uppercase">Grand Total :</th>
                                    {week1Dates.map((_, i) => (
                                        <th key={`gt1-${i}`} className="border border-gray-800 px-1 py-3 text-center font-mono">
                                            {reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.week1Hours[i], 0), 0).toFixed(2)}
                                        </th>
                                    ))}
                                    <th className="border border-gray-800 px-2 py-3 text-center font-mono bg-gray-800">
                                        {reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.totalWk1, 0), 0).toFixed(2)}
                                    </th>
                                    {week2Dates.map((_, i) => (
                                        <th key={`gt2-${i}`} className="border border-gray-800 px-1 py-3 text-center font-mono">
                                            {reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.week2Hours[i], 0), 0).toFixed(2)}
                                        </th>
                                    ))}
                                    <th className="border border-gray-800 px-2 py-3 text-center font-mono bg-gray-800">
                                        {reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.totalWk2, 0), 0).toFixed(2)}
                                    </th>
                                    <th className="border border-gray-800 px-2 py-3 text-center font-mono bg-indigo-900">
                                        {reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.grandTotal, 0), 0).toFixed(2)}
                                    </th>
                                </tr>
                            </thead>
                        </table>
                    </div>
                )}
            </div>

            <style dangerouslySetInnerHTML={{ __html: `
                @media print {
                    @page {
                        size: landscape;
                        margin: 10mm;
                    }
                    body {
                        background: white;
                    }
                    .no-print {
                        display: none !important;
                    }
                    .page-break {
                        page-break-after: always;
                    }
                }
            `}} />
        </div>
    );
};

export default StaffWorkingHoursPrintPage;
